/**
 * AI:
 * Status: "confirmed"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Reusable "Besuch dokumentieren" action button. Opens the metadata-driven
 *   vs.Visit create dialog via MvsUiObjectService.openObjectViaDialog (alias
 *   'vs.Visit'). When placed in a customer context it pre-seeds the customer FK via
 *   the import object context so the backend VisitForm hides the lead field.
 */
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {MvsUiObjectService} from '../../../../core/shared/object/service/mvs-ui-object.service';
import {ObjectIdentifier} from '../../../../core/shared/basic/object-identifier';
import {DtoImportObjectContext} from '../../../../core/shared/dto/dto.import.object.context';
import {MvsCrudModeEnum} from '../../../../core/shared/service/crud/mvs-crud-mode.enum';
import {ObjectChangeInformation} from '../../../../core/shared/logic/object-change-information';
import {VisitDto} from '../../model/dto/entity/visit.dto';
import {VsOwnerTypeEnum} from '../../model/dto/enum/vs-owner-type.enum';

/**
 * "Besuch dokumentieren" action button.
 *
 * Triggers the standard metadata-driven create dialog for {@code vs.Visit}. There is
 * NO manual Angular form here — the backend {@code VisitForm} drives every field and
 * its ownerType-dependent customer/lead visibility.
 *
 * When a {@code customerId} is supplied (e.g. on the customer page or the
 * conversation context panel), the dialog opens with:
 *  - {@code ownerType = customer} pre-seeded on the default DTO, and
 *  - an import object context pointing at the customer, so the backend resolves and
 *    locks the customer FK and hides the lead field.
 *
 * The {@code (visitCreated)} output emits the created/updated visit so a parent can
 * refresh its list or navigate to the new visit.
 *
 * @ticket TM-1000001921
 */
@Component({
    selector: 'mvs-vs-document-visit-action',
    templateUrl: './vs-document-visit-action.component.html',
    standalone: false
})
export class VsDocumentVisitActionComponent implements OnInit {

    /** Optional customer id; when set the visit is pre-bound to this customer. */
    @Input() customerId: number;

    /** Button label (German). */
    @Input() label: string = 'Besuch dokumentieren';

    /** Render as a compact (text/icon-only) button when true. */
    @Input() compact: boolean = false;

    /** Emits the created/updated visit DTO after the dialog reports a change. */
    @Output() visitCreated = new EventEmitter<VisitDto>();

    constructor(protected objectService: MvsUiObjectService) {
    }

    ngOnInit(): void {
    }

    /**
     * Open the vs.Visit create dialog. Pre-seeds the customer owner when a
     * customerId is available so the backend form hides the lead field.
     */
    openVisitDialog(): void {

        let importContext: DtoImportObjectContext = null;
        let defaultDto: VisitDto = null;

        if (this.customerId) {
            const customerIdentifier = new ObjectIdentifier('cr.Customer', this.customerId);
            importContext = DtoImportObjectContext.createFromObjectIdentifier(customerIdentifier);

            defaultDto = new VisitDto();
            defaultDto.ownerType = VsOwnerTypeEnum.customer;
            defaultDto.customerDtoId = this.customerId;
        }

        this.objectService.openObjectViaDialog(
            importContext,
            'vs.Visit',
            0,
            defaultDto,
            false,
            false,
            (event: ObjectChangeInformation) => {
                if (event?.after) {
                    this.visitCreated.emit(event.after as VisitDto);
                }
            },
            MvsCrudModeEnum.create
        );
    }

}
